// ============================================
// TTS Client with Circuit Breaker Pattern
// ============================================

import { API_CONFIG } from '@/constants';
import type { TTSOptions, ErrorType, AppError } from '@/types';

// ============================================
// Circuit Breaker States
// ============================================

type CircuitState = 'closed' | 'open' | 'half-open';

// Abort a TTS fetch that hasn't responded within this window so a hung
// connection can't stall the audio button until the browser's own (much
// longer) timeout — the abort is treated as a retryable failure.
const FETCH_TIMEOUT_MS = 8000;

// True for the DOMException thrown when a fetch is aborted by
// AbortSignal.timeout() (TimeoutError) or an AbortController (AbortError).
function isAbortError(error: unknown): boolean {
  return (
    error instanceof DOMException &&
    (error.name === 'TimeoutError' || error.name === 'AbortError')
  );
}

// ============================================
// TTS Error
// ============================================

export class TTSError extends Error {
  type: ErrorType;
  retryable: boolean;

  constructor(type: ErrorType, message: string, retryable = true) {
    super(message);
    this.name = 'TTSError';
    this.type = type;
    this.retryable = retryable;
  }

  toAppError(): AppError {
    return {
      type: this.type,
      message: this.message,
      recoverable: this.retryable
    };
  }
}

// ============================================
// TTS Client
// ============================================

export class TTSClient {
  private failures = 0;
  private circuitState: CircuitState = 'closed';
  private lastFailureTime = 0;
  private readonly circuitResetTimeout = 30000; // 30 seconds

  // Rate limiting
  private requestTimestamps: number[] = [];
  private readonly rateLimitWindow = 60000; // 1 minute

  // ==========================================
  // Circuit Breaker Logic
  // ==========================================

  private isCircuitOpen(): boolean {
    if (this.circuitState === 'closed') return false;

    // Check if enough time has passed to try again (half-open)
    if (Date.now() - this.lastFailureTime > this.circuitResetTimeout) {
      this.circuitState = 'half-open';
      return false;
    }

    return this.circuitState === 'open';
  }

  private recordSuccess(): void {
    this.failures = 0;
    this.circuitState = 'closed';
  }

  private recordFailure(): void {
    this.failures++;
    this.lastFailureTime = Date.now();

    if (this.failures >= API_CONFIG.circuitBreakerThreshold) {
      this.circuitState = 'open';
      console.warn('TTS Circuit breaker opened after', this.failures, 'failures');
    }
  }

  // ==========================================
  // Rate Limiting
  // ==========================================

  private checkRateLimit(): boolean {
    const now = Date.now();
    // Remove timestamps older than the window
    this.requestTimestamps = this.requestTimestamps.filter(
      ts => now - ts < this.rateLimitWindow
    );

    if (this.requestTimestamps.length >= API_CONFIG.rateLimit) {
      return false;
    }

    this.requestTimestamps.push(now);
    return true;
  }

  // ==========================================
  // Fetch with Retry
  // ==========================================

  private async fetchWithRetry(
    text: string,
    retries = API_CONFIG.maxRetries
  ): Promise<ArrayBuffer> {
    let lastError: Error | null = null;

    for (let attempt = 0; attempt < retries; attempt++) {
      try {
        const response = await fetch(API_CONFIG.ttsEndpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ text }),
          // Older Safari (<16) lacks AbortSignal.timeout — degrade to no
          // timeout there rather than throwing synchronously and exhausting
          // the retry budget for every request.
          signal: typeof AbortSignal.timeout === 'function'
            ? AbortSignal.timeout(FETCH_TIMEOUT_MS)
            : undefined
        });

        if (!response.ok) {
          if (response.status === 429) {
            throw new TTSError('RATE_LIMITED' as ErrorType, 'Rate limited by server', true);
          }
          throw new TTSError('TTS_FAILED' as ErrorType, `Server error: ${response.status}`, true);
        }

        // Server returns JSON with base64-encoded audio
        const data = await response.json();
        const binaryString = atob(data.audio);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        return bytes.buffer as ArrayBuffer;
      } catch (error) {
        // Normalize an abort/timeout into a retryable network error so the
        // final thrown error is typed consistently with other failures.
        lastError = isAbortError(error)
          ? new TTSError('NETWORK' as ErrorType, 'TTS request timed out', true)
          : (error as Error);

        // Don't retry on rate limiting
        if (error instanceof TTSError && error.type === ('RATE_LIMITED' as ErrorType)) {
          throw error;
        }

        // Exponential backoff
        if (attempt < retries - 1) {
          const backoffMs = API_CONFIG.retryBackoffMs * Math.pow(2, attempt);
          await new Promise(resolve => setTimeout(resolve, backoffMs));
        }
      }
    }

    throw lastError || new TTSError('NETWORK' as ErrorType, 'Failed to fetch audio', true);
  }

  // ==========================================
  // Public API
  // ==========================================

  async getAudio(text: string): Promise<Blob> {
    // Check circuit breaker
    if (this.isCircuitOpen()) {
      throw new TTSError(
        'TTS_FAILED' as ErrorType,
        'TTS service temporarily unavailable',
        false
      );
    }

    // Check rate limit
    if (!this.checkRateLimit()) {
      throw new TTSError(
        'RATE_LIMITED' as ErrorType,
        'Too many requests. Please wait.',
        true
      );
    }

    try {
      const audioBuffer = await this.fetchWithRetry(text);
      this.recordSuccess();
      return new Blob([audioBuffer], { type: 'audio/mp3' });
    } catch (error) {
      this.recordFailure();
      throw error;
    }
  }

  async speak(text: string, options: TTSOptions = {}): Promise<void> {
    const { buttonElement, onStart, onEnd, onError } = options;

    // Update button state
    if (buttonElement) {
      buttonElement.classList.add('loading');
    }

    onStart?.();

    try {
      const audioBlob = await this.getAudio(text);
      const audioUrl = URL.createObjectURL(audioBlob);
      const audio = new Audio(audioUrl);

      if (buttonElement) {
        buttonElement.classList.remove('loading');
        buttonElement.classList.add('playing');
      }

      audio.onended = () => {
        URL.revokeObjectURL(audioUrl);
        if (buttonElement) {
          buttonElement.classList.remove('playing');
        }
        onEnd?.();
      };

      audio.onerror = () => {
        URL.revokeObjectURL(audioUrl);
        if (buttonElement) {
          buttonElement.classList.remove('loading', 'playing');
        }
        onError?.(new Error('Audio playback failed'));
      };

      await audio.play();
    } catch (error) {
      if (buttonElement) {
        buttonElement.classList.remove('loading', 'playing');
      }
      onError?.(error as Error);
      throw error;
    }
  }

  // ==========================================
  // Status Methods
  // ==========================================

  getCircuitState(): CircuitState {
    return this.circuitState;
  }

  isAvailable(): boolean {
    return !this.isCircuitOpen();
  }

  resetCircuit(): void {
    this.failures = 0;
    this.circuitState = 'closed';
  }
}

// ============================================
// Singleton Export
// ============================================

export const ttsClient = new TTSClient();
